// Grids & Gantts — the work type's own icon.
//
// The client's structures show it beside every summary, so the pages that
// replaced those structures show it too. The site serves one per work type
// at `/api/icons/work-type/<id>`; when a type has none, or the image fails,
// the fallback is a small lettered chip rather than a broken image or a
// hole where the icon should be — a row must never shift because an icon
// did not load.

import { useEffect, useState } from 'react';
import { FONT } from '../structure/theme';

// Pages that load a hundred thousand rows slim them on arrival and keep only
// the work type's NAME — carrying an icon url on every row would undo the
// slimming. So the icons come from one small request instead: the site's
// work types, resolved by name. 80 of them on this site.
let typesPromise = null;
export function useWorkTypes(api) {
  const [byName, setByName] = useState(null);
  useEffect(() => {
    let on = true;
    if (!typesPromise) {
      typesPromise = Promise.resolve()
        .then(() => (api && api.getIssueTypes ? api.getIssueTypes() : []))
        .then((r) => (Array.isArray(r) ? r : (r && (r.issueTypes || r.data)) || []))
        .catch(() => []);
    }
    typesPromise.then((list) => {
      if (!on) return;
      const m = new Map();
      list.forEach((t) => { if (t && t.name) m.set(String(t.name).trim().toLowerCase(), t); });
      setByName(m);
    });
    return () => { on = false; };
  }, [api]);
  return byName;
}

const COLORS = ['#1868db', '#6554c0', '#00875a', '#bf5b04', '#c9372c', '#0b7a75', '#8f4cbf', '#5e6c84'];
const hashOf = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export const typeOfIssue = (it) => ((it && it.fields && it.fields.issuetype) || null);

export default function TypeIcon({ issue, type, types, size = 14, ui, style }) {
  const own = type || typeOfIssue(issue);
  const [broken, setBroken] = useState(false);
  const name = String((own && own.name) || '');
  // A slimmed row knows only the name; `types` supplies the rest.
  const t = own && own.iconUrl ? own : ((types && types.get(name.trim().toLowerCase())) || own);
  const url = t && t.iconUrl;
  const box = {
    width: size, height: size, flex: `0 0 ${size}px`, borderRadius: 3,
    display: 'inline-block', verticalAlign: 'text-bottom', ...style,
  };
  if (!t) return <span aria-hidden style={{ ...box, background: 'transparent' }} />;
  if (url && !broken) {
    return <img src={url} alt="" title={name} onError={() => setBroken(true)} style={{ ...box, objectFit: 'contain' }} />;
  }
  const letter = name.replace(/[^A-Za-z0-9]/g, '').slice(0, 1).toUpperCase() || '?';
  const bg = COLORS[hashOf(name) % COLORS.length];
  return (
    <span title={name} aria-hidden style={{
      ...box, background: bg, color: '#fff', fontFamily: FONT, fontWeight: 800,
      fontSize: Math.max(8, Math.round(size * 0.62)), lineHeight: `${size}px`, textAlign: 'center',
      border: ui ? `1px solid ${ui.border}` : undefined,
    }}>{letter}</span>
  );
}
